import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Upload, Loader2, CheckCircle2, FileText, AlertTriangle } from 'lucide-react';

interface UploadResp {
  id: string;
  status: string;
  duplicate?: boolean;
  bap_number?: string | null;
}

export default function BerkasUploadPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [classification, setClassification] = useState('TERBATAS');
  const [bapNumber, setBapNumber] = useState('');
  const [caseId, setCaseId] = useState('');
  const [documentType, setDocumentType] = useState('BAP_SAKSI');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);

  const uploadMut = useMutation<UploadResp, Error, File>({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('classification', classification);
      fd.append('document_type', documentType);
      if (bapNumber) fd.append('bap_number', bapNumber);
      if (caseId) fd.append('case_id', caseId);
      const resp = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/documents/upload`, {
        method: 'POST',
        headers: { 'X-Dev-User': 'admin@netra.local' },
        body: fd,
      });
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (file) uploadMut.mutate(file);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const reset = () => {
    uploadMut.reset();
    setFile(null);
    setBapNumber('');
    setCaseId('');
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="max-w-3xl">
      <Link
        to="/berkas"
        className="inline-flex items-center gap-1.5 mb-4 font-mono text-[11px] uppercase tracking-wider text-ink-2 hover:text-brass"
      >
        <ArrowLeft className="w-3 h-3" />
        Kembali ke daftar berkas
      </Link>

      <div className="mb-4 pb-2 border-b-2 border-ink flex items-baseline gap-3 flex-wrap">
        <Upload className="w-5 h-5 text-brass self-center" />
        <h1 className="font-display font-bold text-[22px] leading-tight tracking-tight">Unggah BAP</h1>
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">— Berkas · Ingest Baru</span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-ink-3">OCR → chunk → indexing</span>
      </div>

      {uploadMut.isSuccess && uploadMut.data && (
        <div className="p-5 mb-5 border-2 border-olive bg-olive/5 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-olive shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display font-bold text-[15px] text-olive">
              {uploadMut.data.duplicate ? 'Duplikat terdeteksi' : 'Upload berhasil — ingest pipeline dimulai'}
            </h3>
            <p className="font-mono text-[11px] text-ink-2 mt-1">
              ID: <code className="text-brass">{uploadMut.data.id}</code> · Status: <strong>{uploadMut.data.status}</strong>
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => navigate(`/berkas/${uploadMut.data!.id}`)}
                className="px-3 py-1.5 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold"
              >
                Buka Berkas
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 border border-ink-line text-ink-2 hover:border-ink hover:text-ink font-mono text-[11px] uppercase tracking-wider"
              >
                Upload Lagi
              </button>
            </div>
          </div>
        </div>
      )}

      {uploadMut.isError && (
        <div className="p-4 mb-5 border-2 border-crimson bg-crimson/5 font-mono text-xs text-crimson flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>⚠ {String(uploadMut.error)}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* DROPZONE */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`relative p-8 border-2 border-dashed transition cursor-pointer ${
            dragging ? 'border-brass bg-brass-soft/40' : 'border-ink-line bg-paper-2/30 hover:border-brass'
          }`}
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.tif,.tiff,.bmp,.webp"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setFile(f);
            }}
            className="hidden"
          />
          <div className="text-center">
            {file ? (
              <>
                <FileText className="w-12 h-12 mx-auto mb-3 text-brass" />
                <div className="font-display font-bold text-[16px] mb-1">{file.name}</div>
                <div className="font-mono text-[11px] text-ink-2">
                  {(file.size / 1024).toFixed(1)} KB · {file.type || '—'}
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                  className="mt-3 px-3 py-1 border border-ink-line text-ink-2 hover:border-crimson hover:text-crimson font-mono text-[10px] uppercase tracking-wider"
                >
                  Pilih File Lain
                </button>
              </>
            ) : (
              <>
                <Upload className="w-12 h-12 mx-auto mb-3 text-ink-3" />
                <div className="font-display font-bold text-[16px] mb-1">Drop file di sini</div>
                <div className="font-mono text-[11px] text-ink-2">
                  atau klik untuk pilih · PDF / DOCX / TXT / Gambar (scan) · OCR otomatis
                </div>
              </>
            )}
          </div>
        </div>

        {/* META FIELDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="Nomor LP">
            <input
              type="text"
              value={bapNumber}
              onChange={(e) => setBapNumber(e.target.value)}
              placeholder="LP/B/123/V/2026"
              className="w-full px-3 py-2 border border-ink-line bg-paper text-ink font-mono text-[12px] focus:border-brass outline-none"
            />
          </Field>
          <Field label="Case ID (eksternal)">
            <input
              type="text"
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
              placeholder="LP-123-2026"
              className="w-full px-3 py-2 border border-ink-line bg-paper text-ink font-mono text-[12px] focus:border-brass outline-none"
            />
          </Field>
          <Field label="Jenis Dokumen">
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full px-3 py-2 border border-ink-line bg-paper text-ink font-mono text-[12px] focus:border-brass outline-none"
            >
              <option value="BAP_SAKSI">BAP Saksi</option>
              <option value="BAP_TERSANGKA">BAP Tersangka</option>
              <option value="BAP_KORBAN">BAP Korban</option>
              <option value="BAP_AHLI">BAP Ahli</option>
              <option value="BAP_TKP">BAP TKP</option>
              <option value="BAP_PENANGKAPAN">BAP Penangkapan</option>
              <option value="BAP_PENGGELEDAHAN">BAP Penggeledahan</option>
              <option value="BAP_PENYITAAN">BAP Penyitaan</option>
              <option value="LAINNYA">Lainnya</option>
            </select>
          </Field>
          <Field label="Klasifikasi BSSN">
            <select
              value={classification}
              onChange={(e) => setClassification(e.target.value)}
              className="w-full px-3 py-2 border border-ink-line bg-paper text-ink font-mono text-[12px] focus:border-brass outline-none"
            >
              <option value="BIASA">BIASA</option>
              <option value="TERBATAS">TERBATAS</option>
              <option value="RAHASIA">RAHASIA</option>
              <option value="SANGAT_RAHASIA">SANGAT RAHASIA</option>
            </select>
          </Field>
        </div>

        {/* ACTIONS */}
        <div className="flex items-center gap-3 pt-3 border-t border-dashed border-ink-line">
          <button
            type="submit"
            disabled={!file || uploadMut.isPending}
            className="px-5 py-2.5 font-mono text-[12px] uppercase tracking-wider font-semibold bg-ink text-paper hover:bg-brass transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {uploadMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Mulai Ingest
          </button>
          <button
            type="button"
            onClick={() => navigate('/berkas')}
            className="px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider text-ink-2 hover:text-ink"
          >
            Batal
          </button>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-ink-3">
            Pipeline: OCR / extract → chunk → embed → indexing
          </span>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
